import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";
import useViewport from "src/hooks/use-viewport";
import type { TextAlign, Viewport } from "src/types";
import getViewportValue from "src/utils/get-viewport-value";
import { ThemeContext } from "styled-components";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Action, {
  ActionComponentStyle,
  ACTIONS,
} from "@/components/editor/primitives/legacy/action/action-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import { DEFAULT_BUTTON_TEXT } from "@/components/editor/shared/constants";
import { blackLogo, whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import {
  ColorLabels,
  useInvertedColor,
} from "@/components/editor/utils/use-color";
import WrappedElement from "@/components/element/element-component";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";

import {
  Image,
  Logo,
  LogoWrapper,
  RichTextContainer,
} from "./header-option-2-component-styles";

export type HeaderOption2Props = {
  backgroundImage?: string;
  backgroundImageAlt?: string;
  backgroundPosition?: ImagePosition;
  ctaText: string;
  heading: string;
  isLogoShowing?: boolean;
  logo?: string;
  subheading: string;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => {
  return (
    <HeaderSettings
      optimalImageSize="620x780px"
      backgroundImageSize={{
        width: "120",
        height: "288",
      }}
    />
  );
};

export default function HeaderOption2(props: HeaderOption2Props) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();

  const viewport = useViewport();
  const style = useStyle(viewport);

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "subheading",
    "cta-text",
  ]);

  const theme = React.useContext(ThemeContext);

  const showLightLogo =
    useInvertedColor(theme.colors[ColorLabels.BACKGROUND]) === "#ffffff";

  const defaultLogoImage = showLightLogo ? whiteLogo : blackLogo;

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={nodeId}
      style={style.wrapper}
      data-testid="header-option-2"
    >
      <BlockMaxWidthWrapper>
        <div style={style.containerStyles}>
          <div style={style.contentStyles}>
            {props.isLogoShowing && (
              <LogoWrapper>
                <Logo
                  draggable="false"
                  src={props.logo || defaultLogoImage}
                  alt="Header Logo"
                  data-testid="header-option-2-logo"
                  style={{
                    height: formatLogoSize(props.logoSize),
                    width: "auto",
                  }}
                  isLogoResized={!isNaN(props.logoSize)}
                />
              </LogoWrapper>
            )}
            {isRichTextCompatible ? (
              <RichTextContainer>
                <Element
                  id="heading"
                  is={RichText}
                  text={`<h2>${props.heading}</h2>`}
                />
                <Element
                  id="subheading"
                  is={RichText}
                  text={`<p>${props.subheading}</p>`}
                />

                <Element
                  id="cta-text"
                  is={RichText}
                  text={`<h5>${props.ctaText}</h5>`}
                />
              </RichTextContainer>
            ) : (
              <>
                <WrappedElement
                  id="heading"
                  is={Text}
                  element="h1"
                  text={props.heading}
                  pattern="headingOne"
                  style={style.textStyles}
                />
                <WrappedElement
                  id="subheading"
                  is={Text}
                  text={props.subheading}
                  pattern="paragraph"
                  style={style.textStyles}
                />
                <WrappedElement
                  id="cta-text"
                  is={Text}
                  text={props.ctaText}
                  pattern="headingFour"
                  style={style.textStyles}
                />
              </>
            )}
            <div style={style.ctaButtonWrapper}>
              <WrappedElement
                id="cta-button"
                is={Action}
                element="button"
                text={DEFAULT_BUTTON_TEXT}
                style={style.ctaButtonStyles}
                action={ACTIONS.registrationPage}
                actions={[ACTIONS.registrationPage, ACTIONS.externalLink]}
                showOptions
              />
            </div>
          </div>
          <div style={style.bgImageWrapper}>
            <Image
              imageFit={props.imageFit}
              $position={props.backgroundPosition}
              src={props.backgroundImage}
              alt={props.backgroundImageAlt || ""}
            />
          </div>
        </div>
      </BlockMaxWidthWrapper>
    </BlockBackgroundColorWrapper>
  );
}

const useStyle = (viewport: Viewport) => {
  const style = React.useMemo(
    () => ({
      wrapper: {
        padding: getViewportValue(
          [
            `${rem(48)} ${rem(16)} ${rem(16)}`,
            `${rem(48)} ${rem(16)} ${rem(16)}`,
            `0 0 0 ${rem(24)}`,
          ],
          viewport
        ),
      },
      containerStyles: {
        display: "grid",
        gridTemplateColumns: getViewportValue(
          ["1fr", "1fr", "1.2fr 1fr"],
          viewport
        ),
        gridColumnGap: getViewportValue(["unset", "unset", rem(128)], viewport),
        gridRowGap: getViewportValue([rem(16), rem(32), "unset"], viewport),
      },
      contentStyles: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gridRowGap: rem(24),
        alignContent: "center",
        padding: getViewportValue(
          ["unset", "unset", `${rem(130)} 0`],
          viewport
        ),
      },
      textStyles: {
        textAlign: getViewportValue(
          ["center", "center", "left"],
          viewport
        ) as TextAlign,
      },
      ctaButtonWrapper: {
        margin: getViewportValue(["0 auto", "0 auto", "unset"], viewport),
        width: "100%",
        maxWidth: rem(180),
      },
      ctaButtonStyles: {
        padding: [rem(12), rem(12), `${rem(8)} ${rem(40)}`],
      } as ActionComponentStyle,
      bgImageWrapper: {
        width: "100%",
        maxWidth: getViewportValue(["unset", "unset", rem(610)], viewport),
        height: "100%",
        maxHeight: getViewportValue([rem(440), rem(640), "unset"], viewport),
      },
    }),
    [viewport]
  );

  return style;
};

HeaderOption2.craft = {
  custom: {
    displayName: "Header option #2",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
